import React from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  LabelList,
} from "recharts";
import { Progress } from "antd";
import Loader from "../../common/Loader";

const HEALTH_COLORS = {
  Online: "#059669",
  Offline: "#DC2626",
  Awaiting: "#D97706",
};

const SEVERITY_COLORS = {
  CRITICAL: "#DC2626",
  MAJOR: "#D97706",
  MINOR: "#2563EB",
  INFO: "#0284C7",
};

export const OverviewKpiStrip = ({ items }) => (
  <div className="nms-kpi-strip">
    {items.map((item) => (
      <button
        type="button"
        key={item.label}
        className={`nms-kpi-tile ${item.tone || ""} ${item.active ? "is-active" : ""}`}
        onClick={item.onClick}
        disabled={!item.onClick}
      >
        <span className="nms-kpi-label">{item.label}</span>
        <strong className="nms-kpi-value">{item.value ?? 0}</strong>
        {item.hint && <span className="nms-kpi-hint">{item.hint}</span>}
      </button>
    ))}
  </div>
);

export const DeviceHealthChart = ({
  loading,
  data,
  onSliceClick,
  emptyLabel,
}) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);

  if (loading) return <Loader />;
  if (!total) return <div className="nms-empty">{emptyLabel}</div>;

  return (
    <div className="nms-split-chart">
      <div className="nms-chart-canvas">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius="62%"
              outerRadius="88%"
              paddingAngle={2}
              stroke="none"
            >
              {data.map((entry) => (
                <Cell
                  key={entry.type}
                  fill={HEALTH_COLORS[entry.name] || "#94A3B8"}
                  cursor="pointer"
                  onClick={() => onSliceClick?.(entry.type)}
                />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
        <div className="nms-donut-center">
          <strong>{total}</strong>
          <span>Total</span>
        </div>
      </div>
      <ul className="nms-legend-list">
        {data.map((entry) => (
          <li key={entry.type}>
            <button type="button" onClick={() => onSliceClick?.(entry.type)}>
              <i style={{ background: HEALTH_COLORS[entry.name] }} />
              <span>{entry.name}</span>
              <strong>{entry.value}</strong>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const AlertsChart = ({
  loading,
  faults,
  formatSeverity,
  onSeverityClick,
  emptyLabel,
}) => {
  const data = Array.isArray(faults)
    ? faults.map((item) => ({
        name: formatSeverity(item.alarmSeverityLevel),
        value: item.count,
        level: item.alarmSeverityLevel,
      }))
    : [];
  const total = data.reduce((sum, item) => sum + item.value, 0);

  if (loading) return <Loader />;
  if (!total) return <div className="nms-empty">{emptyLabel}</div>;

  return (
    <div className="nms-split-chart">
      <div className="nms-chart-canvas">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius="62%"
              outerRadius="88%"
              paddingAngle={2}
              stroke="none"
            >
              {data.map((entry) => (
                <Cell
                  key={entry.level}
                  fill={SEVERITY_COLORS[entry.level] || "#94A3B8"}
                  cursor="pointer"
                  onClick={() => onSeverityClick?.(entry.level)}
                />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
        <div className="nms-donut-center">
          <strong>{total}</strong>
          <span>Open</span>
        </div>
      </div>
      <ul className="nms-legend-list">
        {data.map((entry) => (
          <li key={entry.level}>
            <button type="button" onClick={() => onSeverityClick?.(entry.level)}>
              <i style={{ background: SEVERITY_COLORS[entry.level] }} />
              <span>{entry.name}</span>
              <strong>{entry.value}</strong>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const ClientsChart = ({
  loading,
  wiredCount,
  wirelessCount,
  wiredLabel,
  wirelessLabel,
  emptyLabel,
  onBarClick,
}) => {
  const data = [
    { name: wiredLabel, value: wiredCount || 0, filter: "wired" },
    { name: wirelessLabel, value: wirelessCount || 0, filter: "wireless" },
  ];
  const total = data.reduce((sum, item) => sum + item.value, 0);

  if (loading) return <Loader />;
  if (!total) return <div className="nms-empty">{emptyLabel}</div>;

  return (
    <div className="nms-clients-chart">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 28, left: 4, bottom: 0 }}>
          <XAxis type="number" allowDecimals={false} hide />
          <YAxis
            type="category"
            dataKey="name"
            width={78}
            tick={{ fontSize: 12, fill: "currentColor" }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            cursor={{ fill: "transparent" }}
            contentStyle={{
              background: "var(--inc-card-bg)",
              border: "1px solid var(--inc-border)",
              borderRadius: 8,
              color: "var(--inc-card-title-color)",
              boxShadow: "none",
            }}
            labelStyle={{ color: "var(--inc-card-title-color)" }}
            itemStyle={{ color: "var(--inc-body-color)" }}
          />
          <Bar
            dataKey="value"
            radius={[0, 6, 6, 0]}
            barSize={22}
            onClick={(entry) => onBarClick?.(entry?.filter || entry?.payload?.filter)}
          >
            <LabelList dataKey="value" position="right" fontSize={12} fill="currentColor" />
            <Cell fill="#0369A1" />
            <Cell fill="#0EA5E9" />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export const EdgesPanel = ({
  loading,
  edgeCounts,
  labels,
  emptyLabel,
  onActiveClick,
  onInactiveClick,
  onManagedClick,
}) => {
  const total = edgeCounts?.totalCount || 0;
  const activePct = total ? Math.round((edgeCounts.activeCount / total) * 100) : 0;

  if (loading) return <Loader />;
  if (!total) return <div className="nms-empty">{emptyLabel}</div>;

  return (
    <div className="nms-edges-panel">
      <div className="nms-edges-stats">
        <button type="button" className="nms-edge-stat" onClick={onActiveClick}>
          <strong>{edgeCounts.activeCount}</strong>
          <span>{labels.active}</span>
        </button>
        <button type="button" className="nms-edge-stat" onClick={onInactiveClick}>
          <strong>{edgeCounts.inactiveCount}</strong>
          <span>{labels.inactive}</span>
        </button>
        <button type="button" className="nms-edge-stat" onClick={onManagedClick}>
          <strong>{edgeCounts.managedByGateway}</strong>
          <span>{labels.managed}</span>
        </button>
      </div>
      <div className="nms-edges-meter">
        <div className="nms-edges-meter-head">
          <span>Availability</span>
          <strong>{activePct}%</strong>
        </div>
        <Progress
          percent={activePct}
          showInfo={false}
          strokeWidth={10}
          strokeColor="#059669"
          trailColor="#E2E8F0"
        />
      </div>
    </div>
  );
};

export const DeviceInventory = ({
  loading,
  devices,
  emptyLabel,
  onRowClick,
  onViewAll,
  columns,
}) => {
  if (loading) return <Loader />;
  if (!devices?.length) return <div className="nms-empty">{emptyLabel}</div>;

  return (
    <div className="nms-inventory">
      <table>
        <thead>
          <tr>
            <th>{columns.name}</th>
            <th>{columns.type}</th>
            <th>{columns.protocol}</th>
            <th>{columns.status}</th>
          </tr>
        </thead>
        <tbody>
          {devices.map((device, index) => (
            <tr
              key={`${index}-${device.macAddress || device.deviceId || device.deviceName || "device"}`}
              onClick={() => onRowClick?.(device)}
            >
              <td>
                <strong>{device.deviceName || "Unnamed"}</strong>
                <span>{device.ipAddress || device.macAddress || "—"}</span>
              </td>
              <td>{device.deviceType || "—"}</td>
              <td>{device.protocolType || "—"}</td>
              <td>
                <em className={`nms-status ${(device.health || "").toLowerCase()}`}>
                  {device.health || "Unknown"}
                </em>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {devices.length > 12 && (
        <button type="button" className="nms-view-all" onClick={onViewAll}>
          View all {devices.length} devices
        </button>
      )}
    </div>
  );
};
